import React, { useState } from 'react';
import {
  APP_STORE_URL,
  Footer,
  localBusinessSchema,
  organizationSchema,
  PLAY_STORE_URL,
  SEO,
  websiteSchema,
} from '@coupling/ui';
import Header from '../components/Header';
import StripePaymentsSection from '../components/hardware/StripePaymentsSection';
import ContactSalesModal from '../components/ContactSalesModal';
import { useBooking } from '../components/BookingProvider';
import CouplingStorySection from '../components/marketing/CouplingStorySection';
import HeroDeviceCluster from '../components/marketing/HeroDeviceCluster';
import MerchantConnectionSection from '../components/marketing/MerchantConnectionSection';
import PosWorkflowSection from '../components/marketing/PosWorkflowSection';
import PricingFactsSection from '../components/marketing/PricingFactsSection';
import ProductModesSection from '../components/marketing/ProductModesSection';
import { seoConfig } from '../config/seo';
import {
  bonusVoucherCopy,
  demoTopics,
  heroHeadline,
  heroPosChips,
  heroSubhead,
  noTransactionFeesFact,
  ownPosHeadline,
  primaryDemoCta,
} from '../content/siteMessaging';

const HERO_PRELOADS = [
  {
    href: '/product/hero/coupling-hero-desktop-blended.jpg',
    media: '(min-width: 768px)',
    type: 'image/jpeg',
  },
  {
    href: '/product/hero/coupling-hero-mobile.jpg',
    media: '(max-width: 767px)',
    type: 'image/jpeg',
  },
];
const heroValuePoints = [
  {
    title: 'Your own rewards & gift cards',
    description:
      'Create them in minutes and spend them across partner shops nearby.',
  },
  {
    title: ownPosHeadline,
    description:
      'Online orders paid up front or at pickup, subscriptions, tips and tip pooling — one system.',
  },
  {
    title: 'Catalog, checkout, booking',
    description:
      'Sell items, time the order, and let customers book the next appointment.',
  },
];

export const Homepage: React.FC = () => {
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const booking = useBooking();

  const footerColumns = [
    {
      title: 'Product',
      links: [
        { label: 'How Coupling works', href: '/#how-coupling-works' },
        { label: 'What you can run tomorrow', href: '/#product-modes' },
        { label: 'POS workflow', href: '/#pos-workflow' },
        { label: 'Pricing', href: '/#pricing' },
        { label: 'Hardware', href: '/hardware' },
      ],
    },
    {
      title: 'Mobile App',
      links: [
        { label: 'Get the App', href: '/app' },
        { label: 'Google Play', href: PLAY_STORE_URL },
        { label: 'App Store', href: APP_STORE_URL },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Full Guide', href: '/guide' },
        { label: 'How it Works', href: '/how-it-works' },
        { label: 'Blog', href: '/blog' },
      ],
    },
    {
      title: 'Legal',
      links: [{ label: 'Privacy Policy', href: '/privacy' }],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf4]">
      <SEO
        title={`Checkout, appointments, and rewards for WNC merchants - ${seoConfig.siteName}`}
        description={seoConfig.defaultDescription}
        canonical={seoConfig.siteUrl}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={seoConfig.siteUrl}
        preloadImages={HERO_PRELOADS}
        jsonLd={[
          organizationSchema({
            name: seoConfig.siteName,
            url: seoConfig.siteUrl,
            description: seoConfig.defaultDescription,
          }),
          websiteSchema({ name: seoConfig.siteName, url: seoConfig.siteUrl }),
          localBusinessSchema({
            name: seoConfig.siteName,
            url: seoConfig.siteUrl,
            description: seoConfig.defaultDescription,
            address: {
              addressLocality: 'Asheville',
              addressRegion: 'NC',
              addressCountry: 'US',
            },
            areaServed: 'Western North Carolina',
          }),
        ]}
      />
      <Header />

      <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,236,214,0.55),_transparent_32%),linear-gradient(135deg,#c85c2f_0%,#8f3d1b_52%,#5f2a19_100%)] px-4 pb-20 pt-28 sm:pt-32">
        <div
          className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(to_top,rgba(255,250,244,0.98),rgba(255,250,244,0))]"
          aria-hidden="true"
        />
        <div className="absolute inset-x-0 bottom-0" aria-hidden="true">
          <div className="mx-auto h-28 max-w-6xl rounded-t-[100%] bg-[#c16a3c]/25 blur-2xl" />
        </div>

        <div className="relative mx-auto grid max-w-6xl gap-14 lg:grid-cols-[1fr_1.05fr] lg:items-center">
          <div className="text-white">
            <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-1 text-sm font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur-sm">
              For Western North Carolina merchants
            </span>
            <h1 className="mt-6 max-w-3xl text-5xl font-bold tracking-tight text-white sm:text-6xl">
              {heroHeadline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/88 sm:text-xl">
              {heroSubhead}
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {heroValuePoints.map((point) => (
                <article
                  key={point.title}
                  className="rounded-[1.5rem] border border-white/14 bg-white/10 p-4 backdrop-blur-sm"
                >
                  <h3 className="text-base font-semibold text-white">
                    {point.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/80">
                    {point.description}
                  </p>
                </article>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <button
                type="button"
                onClick={() => booking.open('demo')}
                className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-base font-semibold text-brand-primary shadow-xl shadow-black/15 transition-transform hover:-translate-y-0.5"
              >
                {primaryDemoCta}
              </button>
              <button
                type="button"
                onClick={() => {
                  document
                    .getElementById('how-coupling-works')
                    ?.scrollIntoView({
                    behavior: 'smooth',
                  });
                }}
                className="inline-flex items-center justify-center rounded-full border border-white/45 bg-white/10 px-7 py-3 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/18"
              >
                See how it works
              </button>
            </div>

            <button
              type="button"
              onClick={() => setIsSalesOpen(true)}
              className="mt-5 text-sm font-semibold text-white/85 underline-offset-4 transition hover:text-white hover:underline"
            >
              Tell us about your shop
            </button>

            <div className="mt-8 flex flex-wrap gap-3">
              {heroPosChips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-white/18 bg-white/10 px-4 py-2 text-sm font-medium text-white/92 backdrop-blur-sm"
                >
                  {chip}
                </span>
              ))}
              <span className="rounded-full border border-white/18 bg-white/10 px-4 py-2 text-sm font-medium text-white/92 backdrop-blur-sm">
                Tap to Pay on your phone
              </span>
              <span className="rounded-full border border-white/18 bg-white/10 px-4 py-2 text-sm font-medium text-white/92 backdrop-blur-sm">
                {noTransactionFeesFact}
              </span>
            </div>
          </div>

          <HeroDeviceCluster />
        </div>
      </section>

      <ContactSalesModal
        isOpen={isSalesOpen}
        onClose={() => setIsSalesOpen(false)}
      />

      <CouplingStorySection />

      <ProductModesSection />

      <PosWorkflowSection />

      <MerchantConnectionSection />

      <PricingFactsSection />

      <section className="bg-[#fff7ef] px-4 py-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div className="order-2 lg:order-1">
            <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-brand-primary shadow-sm">
              Local merchant spotlight
            </span>
            <h2 className="mt-6 text-4xl font-bold tracking-tight text-neutral-900">
              What Coupling looks like in a real Asheville practice
            </h2>
            <p className="mt-5 text-lg italic leading-relaxed text-neutral-700">
              &quot;I use Coupling for reward points and gift cards at my
              massage therapy practice. My clients love earning rewards they can
              share with friends.&quot;
            </p>
            <p className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-neutral-500">
              Emily Manders, Sacred Hands Alchemy
            </p>
            <p className="mt-6 text-base leading-relaxed text-neutral-600">
              At Sacred Hands Alchemy, customers can scan the sticker, redeem
              rewards, and book the next visit. Checkout and the offer live
              together — the kind of setup a Hendersonville or Asheville shop
              can actually run tomorrow.
            </p>
          </div>

          <div className="order-1 flex justify-center lg:order-2">
            <div className="w-full max-w-lg rounded-[2rem] border border-brand-primary/12 bg-[#fff4e7] p-5 shadow-[0_24px_60px_rgba(130,64,29,0.14)]">
              <img
                src="/screenshots/signage/sacred-hands-sticker.png"
                alt="Sacred Hands Alchemy branded Coupling sticker showing in-store reward and gift-card proof"
                width={1024}
                height={1024}
                className="w-full rounded-[1.4rem] border border-[#ead6c2] bg-white shadow-[0_16px_36px_rgba(130,64,29,0.10)]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <StripePaymentsSection />

      <section className="bg-[linear-gradient(135deg,#a84c26_0%,#6e321c_100%)] px-4 py-24 text-white">
        <div className="mx-auto max-w-5xl rounded-[2.5rem] border border-white/12 bg-white/8 px-8 py-12 text-center shadow-[0_30px_80px_rgba(18,8,5,0.28)] backdrop-blur-sm sm:px-12">
          <span className="inline-flex rounded-full border border-white/18 bg-white/10 px-4 py-1 text-sm font-semibold uppercase tracking-[0.18em] text-white/90">
            Demo-first next step
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
            See how Coupling fits your store before you commit
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg leading-relaxed text-white/88">
            We&apos;ll walk through {demoTopics}, plus rewards, using the way
            you already sell.
          </p>
          <p className="mx-auto mt-5 max-w-2xl text-base font-medium leading-relaxed text-white">
            {bonusVoucherCopy}
          </p>

          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={() => booking.open('demo')}
              className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-base font-semibold text-brand-primary shadow-xl shadow-black/15 transition-transform hover:-translate-y-0.5"
            >
              {primaryDemoCta}
            </button>
            <button
              type="button"
              onClick={() => setIsSalesOpen(true)}
              className="inline-flex items-center justify-center rounded-full border border-white/30 bg-transparent px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10"
            >
              Tell us about your setup
            </button>
          </div>
        </div>
      </section>

      <Footer
        description="Helping WNC merchants take payments, book appointments, and keep more value in mountain communities."
        columns={footerColumns}
        copyright={`© ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
      />
    </div>
  );
};

export default Homepage;
